import { i18n } from '@/i18n';
import { landingBlogJa } from '@/i18n/landing-blog/ja.js';
import { markdownToSpotlightBlocks } from '@/lib/spotlightMarkdown';

type BlogJaArticles = typeof landingBlogJa.articles;
type BlogJaArticle = BlogJaArticles[keyof BlogJaArticles];

const blogBodyJaLoaders = import.meta.glob<string>('@/content/blog/cms/ja/*.md', {
  query: '?raw',
  import: 'default',
});

async function loadBlogBodyJa(slug: string): Promise<string | null> {
  const suffix = `/content/blog/cms/ja/${slug}.md`;
  const key = Object.keys(blogBodyJaLoaders).find((k) => k.endsWith(suffix));
  if (!key) return null;
  const loader = blogBodyJaLoaders[key];
  return loader ? loader() : null;
}

export interface BlogListPost {
  _id: string;
  slug: string;
  title: string;
  excerpt?: unknown;
  image?: string;
  imageAlt?: string;
  publishedAt?: string;
  category?: string;
  categories?: Array<{ title: string }>;
  tags?: string[];
  readTime?: number;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  language?: string;
}

export interface BlogDetailPost extends BlogListPost {
  bodyContent?: unknown[];
  faq?: Array<{ question: string; answer: string }>;
  canonicalUrl?: string;
  ogImage?: string;
  _updatedAt?: string;
  excerptArray?: unknown[];
  excerptString?: unknown;
}

export function isBlogJaLocale(): boolean {
  return i18n.locale === 'ja';
}

function getJaArticle(slug: string): BlogJaArticle | null {
  if (!isBlogJaLocale()) return null;
  return landingBlogJa.articles[slug as keyof typeof landingBlogJa.articles] ?? null;
}

function applyJaArticleFields<T extends BlogListPost>(post: T, ja: BlogJaArticle): T {
  return {
    ...post,
    title: ja.title || post.title,
    excerpt: ja.excerpt ?? post.excerpt,
    imageAlt: ja.imageAlt || post.imageAlt,
    category: ja.category || post.category,
    categories: ja.category ? [{ title: ja.category }] : post.categories,
    seoTitle: ja.seoTitle || post.seoTitle,
    seoDescription: ja.seoDescription || post.seoDescription,
    keywords: ja.keywords?.length ? ja.keywords : post.keywords,
  };
}

/** Convert markdown `[YouTube](url)` + optional italic caption into embed blocks. */
function normalizeMarkdownBodyBlocks(blocks: ReturnType<typeof markdownToSpotlightBlocks>) {
  const out: ReturnType<typeof markdownToSpotlightBlocks> = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (!block) continue;
    if (block.type === 'paragraphHtml') {
      const yt = block.html.match(
        /<a[^>]+href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^"]+)"[^>]*>\s*YouTube\s*<\/a>/i
      );
      if (yt?.[1]) {
        let caption: string | undefined;
        const next = blocks[i + 1];
        if (next?.type === 'paragraphHtml' && next.html.startsWith('<p><em>')) {
          caption = next.html.replace(/^<p><em>|<\/em><\/p>$/g, '');
          i++;
        }
        out.push({
          type: 'youtube',
          url: yt[1],
          title: caption?.split(' — ')[0] || 'YouTube video',
          caption: caption?.includes(' — ') ? caption.split(' — ').slice(1).join(' — ') : undefined,
        });
        continue;
      }
    }
    out.push(block);
  }
  return out;
}

export function applyBlogPostsListLocale<T extends BlogListPost>(posts: T[]): T[] {
  if (!isBlogJaLocale()) return posts;
  return posts.map((post) => {
    if (post.language === 'ja') return post;
    const ja = getJaArticle(post.slug);
    return ja ? applyJaArticleFields(post, ja) : post;
  });
}

export { faqAnswerHtml } from '@/lib/spotlight-locale';

export async function applyBlogPostDetailLocale<T extends BlogDetailPost>(post: T): Promise<T> {
  if (!isBlogJaLocale()) return post;
  if (post.language === 'ja') return post;
  const ja = getJaArticle(post.slug);
  if (!ja) return post;

  let bodyContent = post.bodyContent || [];
  const bodyMd = await loadBlogBodyJa(post.slug);

  if (bodyMd) {
    bodyContent = normalizeMarkdownBodyBlocks(markdownToSpotlightBlocks(bodyMd));
  }

  const localized = applyJaArticleFields(post, ja);

  return {
    ...localized,
    faq: ja.faq?.length ? ja.faq : localized.faq,
    bodyContent,
    excerptArray: undefined,
    excerptString: ja.excerpt ?? localized.excerpt,
    excerpt: ja.excerpt ?? localized.excerpt,
  };
}
